import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Box,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { leadsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const StatusUpdateDialog = ({ lead, onUpdate, onCancel, isDialog = false }) => {
  const [status, setStatus] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConversionDialog, setShowConversionDialog] = useState(false);
  const [converting, setConverting] = useState(false);
  const [conversionLead, setConversionLead] = useState(null);
  const [checkingConversionDetails, setCheckingConversionDetails] = useState(false);
  const [convertingNow, setConvertingNow] = useState(false);
  const [enrollmentUrl, setEnrollmentUrl] = useState('');
  const [convertFees, setConvertFees] = useState('');
  const [convertDiscount, setConvertDiscount] = useState('');
  const [statusMapping, setStatusMapping] = useState({});
  const [statusLabels, setStatusLabels] = useState({});
  const [loadingMapping, setLoadingMapping] = useState(false);

  // Fetch status-to-sub-status mapping on component mount
  React.useEffect(() => {
    const fetchMapping = async () => {
      try {
        setLoadingMapping(true);
        const response = await leadsAPI.getStatusSubStatusMapping();
        if (response.data?.mapping) {
          setStatusMapping(response.data.mapping);
        }
        if (response.data?.statusLabels) {
          setStatusLabels(response.data.statusLabels);
        }
      } catch (error) {
        console.error('Error fetching status mapping:', error);
      } finally {
        setLoadingMapping(false);
      }
    };
    fetchMapping();
  }, []);

  // Reset sub_status only after mapping is loaded, and only if it is invalid for the selected status.
  // Do not clear while mapping is still loading — that would wipe the last saved sub-status.
  React.useEffect(() => {
    if (!status || loadingMapping) return;
    const validSubStatuses = statusMapping[status];
    if (!Array.isArray(validSubStatuses)) return;
    setSubStatus((current) => {
      if (validSubStatuses.length === 0) return '';
      if (current && !validSubStatuses.includes(current)) return '';
      return current;
    });
  }, [status, statusMapping, loadingMapping]);

  React.useEffect(() => {
    if (lead) {
      setStatus(lead.status || '');
      setSubStatus(lead.sub_status || '');
      setNotes('');
      setError('');
    }
  }, [lead]);

  // Load full lead details when conversion dialog opens (to check recommended fields)
  React.useEffect(() => {
    const loadLeadForConversion = async () => {
      if (!showConversionDialog || !lead?.id) return;
      try {
        setCheckingConversionDetails(true);
        const res = await leadsAPI.getById(lead.id);
        setConversionLead(res.data?.lead || null);
      } catch (e) {
        console.error('Failed to load lead details for conversion:', e);
        // Non-blocking: allow conversion even if we cannot fetch details
        setConversionLead(null);
      } finally {
        setCheckingConversionDetails(false);
      }
    };
    loadLeadForConversion();
  }, [showConversionDialog, lead?.id]);

  React.useEffect(() => {
    if (!showConversionDialog) return;
    const l = conversionLead || lead || {};
    setConvertFees(l.fees != null && l.fees !== '' ? String(l.fees) : '');
    setConvertDiscount(l.discount != null && l.discount !== '' ? String(l.discount) : '');
  }, [showConversionDialog, conversionLead, lead]);

  const handleSubmit = async () => {
    if (!status) {
      setError('Please select a status');
      return;
    }

    // Prevent double submission
    if (loading || converting) {
      return;
    }

    // If status is being changed to 'won', show conversion dialog
    if (status === 'won' && lead.status !== 'won') {
      setShowConversionDialog(true);
      return;
    }

    // For other status changes, proceed normally
    await updateStatus();
  };

  const updateStatus = async () => {
    try {
      setLoading(true);
      setError('');

      const updateData = {
        status
      };
      
      // Include sub_status if provided
      if (subStatus && subStatus.trim()) {
        updateData.sub_status = subStatus.trim();
      } else {
        updateData.sub_status = ''; // Clear sub_status if empty
      }
      
      // Only include notes if it's not empty
      if (notes && notes.trim()) {
        updateData.notes = notes.trim();
      }
      
      // Use leadsAPI for leads, studentsService for students
      await leadsAPI.updateStatus(lead.id, updateData);

      toast.success('Status updated successfully');
      // Call onUpdate to refresh the parent component with the saved values
      onUpdate({
        id: lead.id,
        status: updateData.status,
        sub_status: updateData.sub_status || null,
      });
      onCancel(); // Close the dialog
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setError(error.response.data.errors.join(', '));
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to update status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendEnrollmentConfirm = async () => {
    try {
      setConverting(true);
      setError('');

      const enrollmentPayload = {};
      if (convertFees !== '' && !Number.isNaN(Number(convertFees))) {
        enrollmentPayload.fees = parseFloat(convertFees);
      }
      if (convertDiscount !== '' && !Number.isNaN(Number(convertDiscount))) {
        enrollmentPayload.discount = parseFloat(convertDiscount);
      }

      const res = await leadsAPI.generateEnrollmentLink(lead.id, enrollmentPayload);
      const url = res.data?.enrollmentUrl || '';
      setEnrollmentUrl(url);

      toast.success('Enrollment link generated. Lead is now Enrollment Pending; share the link with the parent.');
    } catch (error) {
      console.error('Generate enrollment link error:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to generate enrollment link');
      }
    } finally {
      setConverting(false);
    }
  };

  const handleCopyEnrollmentUrl = async () => {
    if (!enrollmentUrl) return;
    try {
      await navigator.clipboard.writeText(enrollmentUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleConvertNowConfirm = async () => {
    try {
      setConvertingNow(true);
      setError('');
      const body = {};
      if (convertFees !== '' && !Number.isNaN(Number(convertFees))) {
        body.fees = parseFloat(convertFees);
      }
      if (convertDiscount !== '' && !Number.isNaN(Number(convertDiscount))) {
        body.discount = parseFloat(convertDiscount);
      }
      await leadsAPI.convertToStudent(lead.id, body);
      toast.success('Converted to student. Lead is now Won.');
      setShowConversionDialog(false);
      onUpdate();
      onCancel();
    } catch (error) {
      console.error('Convert lead error:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to convert lead');
      }
    } finally {
      setConvertingNow(false);
    }
  };

  const handleConversionCancel = () => {
    const hadUrl = !!enrollmentUrl;
    setShowConversionDialog(false);
    setError('');
    setConversionLead(null);
    setEnrollmentUrl('');
    if (hadUrl) {
      onUpdate();
      onCancel();
    }
  };

  const handleClose = () => {
    if (!loading) {
      onCancel();
    }
  };

  const formContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {status === 'won' && lead.status !== 'won' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Choosing &quot;Won&quot; will generate an enrollment form link and set the lead to &quot;Enrollment Pending&quot;.
            Share the link with the parent. After they submit the form, they will become Won and a student in the Students list.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Lead: {lead?.first_name || 'N/A'} {lead?.last_name || ''}
          </Typography>
          <Chip
            label={`Current Status: ${statusLabels[lead?.status] || lead?.status || 'N/A'}${lead?.sub_status ? ` - ${lead.sub_status}` : ''}`}
            color="primary"
            sx={{ mb: 2 }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>New Status *</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label="New Status *"
              disabled={loadingMapping}
            >
              {Object.entries(statusLabels).length > 0 ? (
                Object.entries(statusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))
              ) : (
                // Fallback status options if mapping hasn't loaded yet
                [
                  { value: 'new', label: 'New' },
                  { value: 'wip_before_demo', label: 'WIP (before demo)' },
                  { value: 'demo_scheduled', label: 'Demo scheduled' },
                  { value: 'demo_reschedule_requested', label: 'Demo reschedule requested' },
                  { value: 'demo_completed', label: 'Demo Completed' },
                  { value: 'demo_not_present', label: 'Demo Not Present' },
                  { value: 'wip_after_demo', label: 'WIP (after demo)' },
                  { value: 'deferred', label: 'Deferred' },
                  { value: 'enrollment_pending', label: 'Enrollment Pending' },
                  { value: 'won', label: 'Won' },
                  { value: 'lost', label: 'Lost' },
                  { value: 'dropped', label: 'Dropped' }
                ].map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        {/* Sub-Status dropdown - only show if status is selected and has sub-status options */}
        {status && statusMapping[status] && statusMapping[status].length > 0 && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Sub-Status</InputLabel>
              <Select
                value={subStatus}
                onChange={(e) => setSubStatus(e.target.value)}
                label="Sub-Status"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {statusMapping[status].map((subStatusOption) => (
                  <MenuItem key={subStatusOption} value={subStatusOption}>
                    {subStatusOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this status change..."
          />
        </Grid>
      </Grid>
    </>
  );

  const conversionDialogContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {checkingConversionDetails ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Checking missing details...
          </Typography>
        </Box>
      ) : null}

      {(() => {
        const l = conversionLead || lead || {};
        const missing = [];

        if (!l.date_of_birth) missing.push('DOB');
        if (!l.class_type_preference) missing.push('Batch preference');
        if (!l.timezone || String(l.timezone).trim() === '') missing.push('Timezone');
        if (!Array.isArray(l.time_preferences) || l.time_preferences.length === 0) missing.push('Availability');

        if (missing.length > 0) {
          return (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Some recommended details are missing:</strong> {missing.join(', ')}
              </Typography>
              <Typography variant="body2">
                You can still generate the enrollment link; they can fill these in when they submit.
              </Typography>
            </Alert>
          );
        }

        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Generate an enrollment form link for this lead? They will be set to &quot;Enrollment Pending&quot;. Share the link with the parent. After they submit the form, they will become Won and a student.
            </Typography>
          </Alert>
        );
      })()}

      <Typography variant="body2" color="text.secondary">
        Lead: <strong>{lead?.first_name || 'N/A'} {lead?.last_name || ''}</strong>
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Fees"
            type="number"
            value={convertFees}
            onChange={(e) => setConvertFees(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            helperText="Saved on student financial details"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Discount (%)"
            type="number"
            value={convertDiscount}
            onChange={(e) => setConvertDiscount(e.target.value)}
            inputProps={{ min: 0, max: 100, step: '0.01' }}
          />
        </Grid>
      </Grid>

      {enrollmentUrl && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Enrollment link"
            value={enrollmentUrl}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyEnrollmentUrl} edge="end" title="Copy link">
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
    </>
  );

  const formActions = (
    <>
      <Button onClick={handleClose} disabled={loading || converting}>
        Cancel
      </Button>
      <Button
        type="button"
        onClick={handleSubmit}
        variant="contained"
        disabled={loading || converting}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        {loading ? 'Updating...' : 'Update Status'}
      </Button>
    </>
  );

  const conversionDialogActions = (
    <>
      <Button onClick={handleConversionCancel} disabled={converting || convertingNow}>
        Go Back
      </Button>
      <Button
        type="button"
        onClick={handleConvertNowConfirm}
        variant="outlined"
        color="success"
        disabled={converting || convertingNow}
        startIcon={convertingNow ? <CircularProgress size={20} /> : null}
      >
        {convertingNow ? 'Converting...' : 'Mark Won (convert now)'}
      </Button>
      {!enrollmentUrl && (
        <Button
          type="button"
          onClick={handleSendEnrollmentConfirm}
          variant="contained"
          color="primary"
          disabled={converting || convertingNow}
          startIcon={converting ? <CircularProgress size={20} /> : null}
        >
          {converting ? 'Generating...' : 'Generate enrollment link'}
        </Button>
      )}
      {enrollmentUrl && (
        <Button
          type="button"
          onClick={handleCopyEnrollmentUrl}
          variant="contained"
          color="primary"
        >
          Copy link
        </Button>
      )}
    </>
  );

  // Show send-enrollment dialog when status is being changed to 'won'
  if (showConversionDialog) {
    return (
      <Dialog open={true} onClose={handleConversionCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Generate enrollment link
        </DialogTitle>
        <DialogContent>
          {conversionDialogContent}
        </DialogContent>
        <DialogActions>
          {conversionDialogActions}
        </DialogActions>
      </Dialog>
    );
  }

  if (isDialog) {
    return (
      <Dialog open={true} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Lead Status
        </DialogTitle>
        <DialogContent>
          {formContent}
        </DialogContent>
        <DialogActions>
          {formActions}
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      {formContent}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        {formActions}
      </Box>
    </>
  );
};

export default StatusUpdateDialog;
